<template>
  <div>
    <Table
      :tableHeaders="tableHeaders"
      :tableData="tableData"
      :initialValues="initialValues"
      :tableFooter="false"
    >
      <template #tableRow="{ request }">
        <tr class="font-sans text-xs border-b border-brand-gray-300 w-full">
          <td class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300">
            <div class="flex items-center gap-4">
              <span class="text-primary cursor-pointer">
                <i
                  :class="[checkExpired(request) && 'fa-exclamation-circle text-red-400']"
                  class="fa"
                ></i>
                <i
                  :class="[
                    checkNearlyExpired(request) && 'fa-exclamation-triangle  text-orange-400'
                  ]"
                  class="fa"
                ></i>
              </span>
            </div>
          </td>
          <slot name="tableRow" :request="request">
            <td
              class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
            >
              {{ request.englishName }}
              {{ request.arabicName }}
              {{ $t('uid') }} : {{ request.unifiedNumber }}
              <div v-if="request.personIdentityNumber">
                {{ $t('identityNumber') }} : {{ request.personIdentityNumber }}
              </div>
            </td>
            <td
              class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
            >
              <div>{{ getLookupText(props.modules, request.module.id) }}</div>
              <div v-show="getLookupText(props.parentService, request?.parentService?.id)">
                - {{ getLookupText(props.parentService, request?.parentService?.id) }}
              </div>
              <div v-show="getLookupText(props.service, request?.service?.id)">
                - {{ getLookupText(props.service, request?.service?.id) }}
              </div>
            </td>
            <td
              class="px-6 py-2 text-gray-700 text-xs font-normal border-r border-brand-gray-300 text-wrap"
            >
              {{ formattedDate(request?.fileIssueDate) }}
            </td>
            <td
              class="px-6 py-2 text-gray-700 text-xs font-normal border-r border-brand-gray-300 text-wrap"
            >
              {{ formattedDate(request?.fileExpiryDate) }}
            </td>
            <td
              class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
            >
              {{ getLookupText(props.countries, request.nationality.id) }}
            </td>
            <td
              class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
            >
              {{ getLookupText(props.serviceStatus, request.serviceStatus.id) }}
            </td>
            <td
              class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
            >
              <div v-show="request.sponsoredViolatedFlag">{{ $t('hasFine') }}</div>
              <div v-show="!request.sponsoredViolatedFlag">{{ $t('noFine') }}</div>
              <div v-show="request.sponsoredViolatedFlag && displayViolationNote">
                <i class="fa fa-info-circle"></i>
              </div>
              <div v-show="request.finesAmount && request.overStayDays">
                {{ $t('NumberOfDay') }} : {{ request.overStayDays }} {{ $t('FinesAmount') }} :
                {{ request.finesAmount }}
              </div>
            </td>
          </slot>

          <td class="px-6 py-2">
            <div class="flex items-center justify-center">
              <div
                class="flex items-center justify-center gap-2 size-10"
                v-if="isShowLockFeature"
              ></div>
              <div class="flex items-center justify-center gap-2 w-20 h-10">
                <slot name="tableOperation" :request="request">
                  <RouterLink
                    v-if="
                      !['oleSponsoredPage', 'canceledEscapeReportPage'].includes(pageType) &&
                      isResidency(request.module)
                    "
                    class="flex justify-center items-center bg-primary size-6 rounded-md text-white cursor-pointer text-center"
                    :to="{ name: 'EstResidencyView', query: { fileNumber: request.fileNumber } }"
                  >
                    <i class="fa fa-eye"></i>
                  </RouterLink>
                  <RouterLink
                    v-if="
                      !['oleSponsoredPage', 'canceledEscapeReportPage'].includes(pageType) &&
                      isVisa(request.module)
                    "
                    class="flex justify-center items-center bg-primary size-6 rounded-md text-white cursor-pointer text-center"
                    :to="{ name: 'EstVisaView', query: { fileNumber: request.fileNumber } }"
                  >
                    <i class="fa fa-eye"></i>
                  </RouterLink>

                  <RouterLink
                    v-if="
                      ![
                        'oleSponsoredPage',
                        'cancelPage',
                        'canceledEscapeReportPage',
                        'escapeFilesPage'
                      ].includes(pageType) && request.personIdentityNumber
                    "
                    class="flex justify-center items-center bg-primary size-6 rounded-md text-white cursor-pointer"
                    :to="{
                      name: 'EstDocumentWallet',
                      query: { employeeUN: request.unifiedNumber }
                    }"
                  >
                    <i class="fa fa-id-card-o"></i>
                  </RouterLink>

                  <tippy
                    :theme="
                      useThemeStore().currentScheme == 'light' ? 'light-border' : 'dark-border'
                    "
                    :arrow="false"
                    :offset="[0, 4]"
                    interactive
                    trigger="click"
                    v-bind="{ ...(maxDate && { maxDate: new Date() }) }"
                    placement="bottom-end"
                  >
                    <button class="rounded p-1 bg-gray-600" @click.prevent="">
                      <faBars class="w-3 fill-white" />
                    </button>
                    <template #content>
                      <div
                        class="flex flex-col gap-y-2 max-h-[12rem] overflow-y-auto"
                        v-if="
                          pageType != 'oleSponsoredPage' &&
                          newBehaviorFilterActions(request.usingNewBehaviorForFilterActions)
                        "
                      >
                        <button
                          :key="key"
                          v-for="(serviceTransaction, key) in request.allowedServiceTransactions"
                        >
                          <div
                            v-if="isShowCanceledReport(serviceTransaction)"
                            @click="process(request, serviceTransaction)"
                            class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                          >
                            {{ getLookupText(props.modules, request.module.id) }} -
                            {{
                              getLookupText(
                                props.transaction,
                                serviceTransaction?.transactionType?.nameId,
                                'nameId'
                              )
                            }}
                          </div>
                        </button>
                      </div>

                      <div
                        class="flex flex-col gap-y-2 max-h-[12rem] overflow-y-auto"
                        v-if="pageType == 'oleSponsoredPage'"
                      >
                        <button
                          v-if="request.module.id == lookups.module.visa"
                          class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                          @click="
                            oldSponsorProcess(
                              request,
                              lookups.ServiceTransactions.reportsVisaDetails
                            )
                          "
                        >
                          {{ $t('EntryExitReport') }}
                        </button>
                        <button
                          v-if="request.module.id == lookups.module.residency"
                          class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                          @click="
                            oldSponsorProcess(
                              request,
                              lookups.ServiceTransactions.reportsResidenceDetails
                            )
                          "
                        >
                          {{ $t('EntryExitReport') }}
                        </button>
                        <button
                          class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                          @click="oldSponsorProcess(request, lookups.ServiceTransactions.entryExit)"
                        >
                          {{ $t('EntryExitReport') }}
                        </button>
                      </div>
                    </template>
                  </tippy>
                </slot>
              </div>
            </div>
          </td>
        </tr>
        <ValidUsers :requestDraft="request" v-if="request?.id === selectedDraft" />
      </template>
    </Table>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import Table from '@/components/Table/index.vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { DateTime } from 'luxon'
import apiHelperService from '@/services'
import client_config from '@/assets/json/client_config.json'
import lookups from '@/assets/json/clientLookups.json'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import faBars from '@/assets/icons/faBars.svg?component'
import useModal from '@/composables/useModal'

const i18n = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const props = defineProps({
  pageType: String,
  tableData: {
    type: Array,
    default: () => []
  },
  initialValues: {
    type: Object,
    default: () => {}
  },
  pageLength: {
    type: Number,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  showModal: {
    type: Boolean,
    default: false
  },
  modules: {
    type: Array
  },
  parentService: {
    type: Array
  },
  service: {
    type: Array
  },
  countries: {
    type: Array
  },
  serviceStatus: {
    type: Array
  },
  transaction: {
    type: Array
  }
})

const tableHeaders = ref([
  {
    value: 1,
    label: ''
  },
  {
    value: 2,
    label: i18n.t('employeename')
  },
  {
    value: 3,
    label: i18n.t('moduleType')
  },
  {
    value: 4,
    label: i18n.t('issueDate')
  },
  {
    value: 5,
    label: i18n.t('expireDate')
  },
  {
    value: 6,
    label: i18n.t('nationality')
  },
  {
    value: 6,
    label: i18n.t('fileStatus')
  },
  {
    value: 6,
    label: i18n.t('fineStatus')
  }
])

const displayViolationNote = ref(false)
const enableFileInfoToSponsored = ref(false)
const enableGetSponsoredServicesUsingFiltereActions = ref(
  client_config.enableGetSponsoredServicesUsingFiltereActions
)
const isUser = ref()

const isShowCanceledReport = (serviceTransaction) => {
  if (props.pageType == 'canceledEscapeReportPage') {
    return [201, 203].includes(serviceTransaction?.serviceTransactionId)
  }
  return true
}

const dialogProps = reactive({
  title: i18n.t('confirmation'),
  label: i18n.t('renewIdForDiffDepartmentsMssg')
})

const getPathInfo = async (employee, serviceTransaction, path) => {
  return await apiHelperService.get(
    `/api/establishment/${employee.unifiedNumber}/administrativeRegionSponsored`,
    {
      params: {
        serviceTransId: serviceTransaction.serviceTransactionId
      }
    }
  )
}

const getConfig = async (applicationId, serviceTransactionId, administrativeRegionId) => {
  return await apiHelperService.get('/api/service/transactions/config', {
    params: {
      applicationId,
      serviceTransactionId,
      administrativeRegionId
    }
  })
}

const oldSponsorProcess = async (employee, serviceTransactionId) => {
  const tempfileexpiryDate = employee.fileExpiryDate >= new Date().toISOString()
  let config
  try {
    config = await getConfig(null, serviceTransactionId, null)
  } catch (error) {
    console.error('error', error)
  }
  router.push({
    name: 'ServiceForm',
    query: {
      unifiedNumber: employee.unifiedNumber,
      isCanceled: '2',
      withException: route.query.withException ? true : false,
      ...(tempfileexpiryDate && !config.secondaryRequestUrl && { expD: tempfileexpiryDate }),
      ...(!config.secondaryRequestUrl && { fileNumber: employee.fileNumber })
    },
    params: {
      serviceId: serviceTransactionId
    }
  })
}

const cancelProcess = async (employee, { serviceTransactionId } = serviceTransaction) => {
  const tempfileexpiryDate = employee.fileExpiryDate >= new Date().toISOString()
  let config
  try {
    config = await getConfig(null, serviceTransactionId, null)
  } catch (error) {
    console.error('error', error)
  }
  router.push({
    name: 'ServiceForm',
    query: {
      unifiedNumber: employee.unifiedNumber,
      fileIsCancelled: true,
      withException: route.query.withException ? true : false,
      ...(tempfileexpiryDate && !config.secondaryRequestUrl && { expD: tempfileexpiryDate }),
      ...(!config.secondaryRequestUrl && { fileNumber: employee.fileNumber })
    },
    params: {
      serviceId: serviceTransactionId
    }
  })
}

const canceledEscapeReportProcess = async (
  employee,
  { serviceTransactionId } = serviceTransaction
) => {
  const tempfileexpiryDate = employee.fileExpiryDate >= new Date().toISOString()
  let config
  try {
    config = await getConfig(null, serviceTransactionId, null)
  } catch (error) {
    console.error('error', error)
  }
  router.push({
    name: 'ServiceForm',
    query: {
      unifiedNumber: employee.unifiedNumber,
      withException: route.query.withException ? true : false,
      ...(tempfileexpiryDate && !config.secondaryRequestUrl && { expD: tempfileexpiryDate }),
      ...(!config.secondaryRequestUrl && { fileNumber: employee.fileNumber })
    },
    params: {
      serviceId: serviceTransactionId
    }
  })
}

const process = async (employee, serviceTransaction) => {
  if (props.pageType == 'canceledEscapeReportPage') {
    canceledEscapeReportProcess(employee, serviceTransaction)
    return
  }
  if (props.pageType == 'cancelPage') {
    cancelProcess(employee, serviceTransaction)
    return
  }
  const transactionId = route.params.serviceId
  const {
    ServiceTransactions: { reportsResidenceDetails, reportsVisaDetails, cancelLeavePermit }
  } = lookups
  const matchService = [reportsResidenceDetails, reportsVisaDetails].includes(transactionId)

  if (matchService) {
    isUser.value = authStore.user.identityNumber == employee.personIdentityNumber
  }

  if (cancelLeavePermit == serviceTransaction.serviceTransactionId) {
    const res = await getPathInfo(employee, serviceTransaction)
    redirectToAction(res, employee, serviceTransaction)
  } else {
    const res = await getPathInfo(employee, serviceTransaction)
    if (!res.isSponsorAdminRegion) {
      redirectToAction(res.adminRegionId, employee, serviceTransaction)
    } else {
      try {
        const { confirm } = useModal(dialogProps)
        await confirm()
        redirectToAction(res.adminRegionId, employee, serviceTransaction)
      } catch (error) {
        console.error('cancel confirm')
      }
    }
  }
}

const loadConfig = async (serviceTransactionId, administrativeRegionId) => {
  return await apiHelperService.get('/api/service/transactions/config', {
    params: {
      serviceTransactionId,
      administrativeRegionId
    }
  })
}

const redirectToAction = async (result, employee, serviceTransaction) => {
  const {
    ServiceTransactions: { cancelLeavePermit }
  } = lookups
  const dateNow = new Date().toISOString()
  const isExpiry = employee.fileExpiryDate < dateNow
  const config = await loadConfig(serviceTransaction.serviceTransactionId, result)
  const adminRegion = result
  const shortNameV2 = config.servicesV2UpdatesSetup
  let requestUrl

  const cancelNum = route.query.isCanceled
    ? route.query.isCanceled
    : cancelLeavePermit == serviceTransaction.serviceTransactionId
      ? 4
      : 0
  if (config.secondaryRequestUrl && shortNameV2?.enableServicesV2UpdatesProcess) {
    const matchService = shortNameV2?.servicesList?.includes(
      +serviceTransaction.serviceTransactionId
    )
    if (matchService) {
      //TODO /oneClick/services/cancelResidency/:serviceTransactionId/step1
      router.push({
        name: 'ServiceForm',
        query: {
          withException: route.query.withException == 'true' ? true : false,
          isCanceled: '0',
          isUser: isUser.value,
          fileNumber: employee.fileNumber,
          sponsoredUN: employee.unifiedNumber,
          administrativeRegionId: adminRegion
        },
        params: {
          serviceId: serviceTransaction.serviceTransactionId
        }
      })
    } else {
      router.push({
        name: 'ServiceForm',
        query: {
          withException: route.query.withException ? true : false,
          isCanceled: cancelNum,
          isUser: isUser.value,
          fileNumber: employee.fileNumber,
          fileModuleId: employee.module.id,
          sponsoredUN: employee.unifiedNumber,
          administrativeRegionId: adminRegion
        },
        params: {
          serviceId: serviceTransaction.serviceTransactionId
        }
      })
    }
  } else if (
    config.secondaryRequestUrl &&
    (client_config.v2ServiceFormTransactionIds.includes(+serviceTransaction.serviceTransactionId) ||
      client_config.v2ServiceFormTransactionIds.includes(+serviceTransaction.serviceTransactionId))
  ) {
    router.push({
      name: 'ServiceForm',
      query: {
        withException: route.query.withException == 'true' ? true : false,
        isCanceled: '0',
        isUser: isUser.value,
        fileNumber: employee.fileNumber,
        fileModuleId: employee.module.id,
        employeeUN: employee.unifiedNumber,
        administrativeRegionId: adminRegion,
        personIdentityNumber: employee.personIdentityNumber,
        englishName: employee.englishName,
        arabicName: employee.arabicName
      },
      params: {
        serviceId: serviceTransaction.serviceTransactionId
      }
    })
  } else if (
    config.secondaryRequestUrl &&
    client_config.enableEidaRevampForServiceTransactionIds.includes(
      +serviceTransaction.serviceTransactionId
    )
  ) {
    router.push({
      name: 'ServiceForm',
      query: {
        withException: route.query.withException == 'true' ? true : false,
        isCanceled: '0',
        isUser: isUser.value,
        fileNumber: employee.fileNumber,
        sponsoredUN: employee.unifiedNumber,
        administrativeRegionId: adminRegion
      },
      params: {
        serviceId: serviceTransaction.serviceTransactionId
      }
    })
  } else {
    router.push({
      name: 'ServiceForm',
      query: {
        expD: true,
        withException: route.query.withException == 'true' ? true : false,
        isCanceled: cancelNum,
        isUser: isUser.value,
        fileNumber: employee.fileNumber,
        fileModuleId: employee.module.id,
        employeeUN: employee.unifiedNumber,
        administrativeRegionId: adminRegion
      },
      params: {
        serviceId: serviceTransaction.serviceTransactionId
      }
    })
  }
}

const newBehaviorFilterActions = (usingNewBehaviorForFilterActions) => {
  const res =
    !enableGetSponsoredServicesUsingFiltereActions.value && !usingNewBehaviorForFilterActions
  return res
}

const checkExpired = (request) => {
  const { fileExpiryDate } = request
  const { residenceServiceTransactions, visaEmploymentServiceTransactions } = lookups
  const transactionId = route.params.serviceId

  if (!fileExpiryDate) return false
  if (
    transactionId &&
    [residenceServiceTransactions.renew, visaEmploymentServiceTransactions.renew].includes(
      transactionId
    )
  ) {
    return false
  } else {
    const fileExpiryDateObj = DateTime.fromJSDate(new Date(fileExpiryDate))
    let isLarge
    Object.values(
      DateTime.now().diff(fileExpiryDateObj, ['years', 'months', 'days']).toObject()
    ).map((v) => {
      v > 0 && (isLarge = true)
    })
    return isLarge
  }
}

const checkNearlyExpired = (employee) => {
  const { module, lookupServiceStatusId } = employee
  const { residencyNearlyExpired, visaNearlyExpired } = lookups.lookupServiceStatus

  return (
    (module.id == lookups.module.residency && lookupServiceStatusId == residencyNearlyExpired) ||
    (module.id == lookups.module.visa && lookupServiceStatusId == visaNearlyExpired)
  )
}

const getLookupText = (entry, id, key) => {
  return entry?.find((v) => (key ? v[key] == id : v?.id == id))?.text || ''
}

const formattedDate = (date) => {
  if (!date) return
  return DateTime.fromISO(date).toFormat('dd/MM/yyyy')
}

const getDisplayViolationNotePreferences = async () => {
  const res = apiHelperService.get(
    'shared/db-pref-control-status/DISPLAY_USERS_VIOLATIONS_NOTES_FOR_SPONSORED'
  )
  displayViolationNote.value = res
}

const isResidency = (module) => {
  if (route.query.moduleId) {
    return false
  }

  return module.id == lookups.module.residency
}
const isVisa = (module) => {
  if (route.query.moduleId) {
    return false
  }

  return module.id == lookups.module.visa
}

const setEnableFileInfoToSponsored = () => {
  if (authStore.user?.applicationId == lookups.application.dubaiResident) {
    enableFileInfoToSponsored.value = false
  } else {
    enableFileInfoToSponsored.value = client_config.enableFileInfoToSponsored
  }
}

onMounted(() => {
  setEnableFileInfoToSponsored()
  getDisplayViolationNotePreferences()
})
</script>

<style></style>
